import {
	Button,
	Heading,
	Tag,
	TagGroup,
	Text,
	TextArea,
	TextField,
} from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import type { Contact } from '@/spaces/contacts/types';
import { getDisplayName } from '@/spaces/contacts/utils';
import { InfoRow } from '@/spaces/people/InfoRow';

import { EditableCard } from '../brand/EditableCard';
import { CARD, cardTitleStyles } from '../brand/styles';

import type {
	Audience,
	AudiencePatch,
	PaginatedMembersResponse,
} from './types';
import { formatDate, formatSource, getMemberCount } from './utils';

const MEMBERS_PAGE_SIZE = 50;

interface AudienceDetailProps {
	audience: Audience;
	userId: string;
	isSaving?: boolean;
	saveError?: string | null;
	onSave?: (updates: AudiencePatch) => Promise<void> | void;
	onCancel?: () => void;
	isNew?: boolean;
}

export const AudienceDetail = memo(function AudienceDetail({
	audience,
	userId,
	isSaving = false,
	saveError = null,
	onSave,
	onCancel,
	isNew = false,
}: AudienceDetailProps) {
	const bridgewayApi = useBridgewayApi();

	const currentDraft = useMemo(
		() => ({
			name: audience.name ?? '',
			description: audience.description ?? '',
		}),
		[audience.name, audience.description],
	);
	const [isEditing, setIsEditing] = useState(isNew);
	const [draft, setDraft] = useState(currentDraft);
	const [nameError, setNameError] = useState<string | null>(null);

	const [members, setMembers] = useState<Contact[]>([]);
	const [isLoadingMembers, setIsLoadingMembers] = useState(false);

	useEffect(() => {
		setDraft(currentDraft);
		if (!isNew) {
			setIsEditing(false);
		}
	}, [currentDraft, isNew]);

	useEffect(() => {
		const fetchMembers = async () => {
			if (!audience.id) {
				setMembers([]);
				return;
			}
			setIsLoadingMembers(true);
			try {
				const params = new URLSearchParams({
					page_size: String(MEMBERS_PAGE_SIZE),
					status: 'active',
				});
				const response = await bridgewayApi.get<PaginatedMembersResponse>(
					`/api/user/${encodeURIComponent(userId)}/audiences/${encodeURIComponent(audience.id)}/members?${params.toString()}`,
				);

				const contactIds = response.members.map((m) => m.contact_id);
				if (contactIds.length > 0) {
					const contacts = await bridgewayApi.post<Contact[]>(
						`/api/user/${encodeURIComponent(userId)}/contacts/batch`,
						{ ids: contactIds },
					);
					setMembers(contacts);
				} else {
					setMembers([]);
				}
			} catch {
				setMembers([]);
			} finally {
				setIsLoadingMembers(false);
			}
		};
		void fetchMembers();
	}, [audience.id, bridgewayApi, userId]);

	const handleSave = useCallback(async () => {
		const updates: AudiencePatch = {};
		const nextName = draft.name.trim();
		const nextDescription = draft.description.trim();

		if (!nextName) return;

		setNameError(null);

		const nameChanged = nextName !== currentDraft.name;
		if (nameChanged) {
			try {
				const existing = await bridgewayApi.get<Audience>(
					`/api/user/${encodeURIComponent(userId)}/audiences/by-name/${encodeURIComponent(nextName)}`,
				);
				if (existing.id !== audience.id) {
					setNameError('An audience with this name already exists');
					return;
				}
			} catch {
				// 404 means name is available — continue
			}
			updates.name = nextName;
		}
		if (nextDescription !== currentDraft.description) {
			updates.description = nextDescription;
		}

		if (Object.keys(updates).length === 0) {
			setIsEditing(false);
			return;
		}

		try {
			await onSave?.(updates);
			setIsEditing(false);
		} catch {
			// PeopleSpace owns and displays the API error.
		}
	}, [audience.id, bridgewayApi, currentDraft, draft, onSave, userId]);

	const handleCancel = useCallback(() => {
		if (isNew && onCancel) {
			onCancel();
			return;
		}
		setDraft(currentDraft);
		setIsEditing(false);
	}, [currentDraft, isNew, onCancel]);

	const editFields = (
		<div className="flex flex-col gap-3">
			<TextField
				label="Name"
				value={draft.name}
				isRequired
				maxLength={100}
				// `isInvalid`, not `validationState` — S2 renamed it, and the old prop was
				// being dropped, so the invalid styling never actually applied.
				isInvalid={!draft.name.trim() || !!nameError}
				errorMessage={nameError || 'Name is required'}
				onChange={(value) => {
					setNameError(null);
					setDraft((prev) => ({ ...prev, name: value }));
				}}
			/>
			<TextArea
				label="Description"
				value={draft.description}
				maxLength={500}
				onChange={(value) =>
					setDraft((prev) => ({ ...prev, description: value }))
				}
			/>
			{saveError && (
				<Text
					styles={style({
						font: 'body',
						fontSize: 'body-sm',
						color: 'negative',
					})}
				>
					{saveError}
				</Text>
			)}
		</div>
	);

	const readFields = (
		<div className="flex flex-col">
			<InfoRow label="Name" value={audience.name || '—'} />
			<InfoRow
				label="Description"
				value={audience.description || '—'}
				isPlaceholder={!audience.description}
				isLast
			/>
		</div>
	);

	const saveDisabled = isSaving || !draft.name.trim();

	return (
		<div
			className="flex h-full min-h-0 flex-col gap-[22px] overflow-y-auto py-4"
		>
			{isNew ? (
				<div className={`${CARD} flex flex-col gap-3`}>
					<Heading level={3} styles={cardTitleStyles}>
						Audience Info
					</Heading>
					{editFields}
					<div className="flex justify-end gap-2 pt-2">
						<Button
							variant="secondary"
							fillStyle="outline"
							isDisabled={isSaving}
							onPress={handleCancel}
						>
							Cancel
						</Button>
						<Button
							variant="accent"
							isDisabled={saveDisabled}
							onPress={handleSave}
						>
							{isSaving ? 'Creating...' : 'Create'}
						</Button>
					</div>
				</div>
			) : (
				<>
					<EditableCard
						title="Audience Info"
						isEditing={isEditing}
						onEdit={() => setIsEditing(true)}
						onSave={() => {
							if (saveDisabled) return;
							void handleSave();
						}}
						onCancel={handleCancel}
					>
						{isEditing ? editFields : readFields}
					</EditableCard>

					<div className={`${CARD} flex flex-col gap-3`}>
						<Heading level={3} styles={cardTitleStyles}>
							Activity
						</Heading>
						<div className="flex flex-col">
							<InfoRow label="Source" value={formatSource(audience.source)} />
							<InfoRow
								label="Created"
								value={formatDate(audience.created_at)}
								isLast
							/>
						</div>
					</div>

					<div className={`${CARD} flex flex-col gap-3`}>
						<Heading level={3} styles={cardTitleStyles}>
							Members ({getMemberCount(audience)})
						</Heading>

						{isLoadingMembers ? (
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'gray-500',
								})}
							>
								Loading members...
							</Text>
						) : members.length > 0 ? (
							<TagGroup size="S">
								{members.map((member) => (
									<Tag key={member.id} id={member.id}>
										{getDisplayName(member)}
									</Tag>
								))}
							</TagGroup>
						) : (
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'gray-500',
								})}
								UNSAFE_className="italic"
							>
								No members yet. Add contacts from the Contacts tab.
							</Text>
						)}
					</div>
				</>
			)}
		</div>
	);
});
